import type { ValueFormat } from '../types';

import { compact, exact, isNumeric } from './format';

/**
 * How a single figure should read.
 *
 * Formatting used to live inside each template, which is why a rating could not
 * be stars without editing `rankedColumns`, and why the hand-written KPI
 * fragment reimplemented K/M rounding in its own inline script. One module, so
 * there is one place to fix and one place to test.
 *
 * `formatValue` always returns a string. A rating's *stars* are a render
 * concern (see `Stars`), so this returns the number — which is what the star
 * component's `aria-label` and any tooltip or table cell should say anyway.
 */
export const formatValue = (value: unknown, format?: ValueFormat): string => {
  if (value === null || value === undefined || value === '') return '—';

  const kind = format?.kind ?? 'number';

  // Anything non-numeric is shown as it came. A rank arrives as "#1" already
  // formatted upstream, and a country name is not a number pretending to be one.
  if (!isNumeric(value)) {
    const text = String(value);
    if (kind === 'rank' && text && !text.startsWith('#')) return `#${text}`;
    return text;
  }

  switch (kind) {
    case 'percent': {
      // Upstream sends percentages two ways: 0.87 and 87, so this has to know
      // which. An explicit `scale` is the answer; the magnitude guess is only
      // the fallback, because it is WRONG above 100% — a genuine 250% arriving
      // as 2.5 takes the ">1 means already scaled" branch and renders "2.5%",
      // a hundredfold understatement on exactly the kind of figure a KPI strip
      // exists to show. The guess stays the default so charts saved before
      // `scale` existed read as they did.
      const scale = format && 'scale' in format ? format.scale : undefined;
      const alreadyPercent = scale ? scale === 'percent' : value > 1;
      const pct = alreadyPercent ? value : value * 100;
      const decimals = format && 'decimals' in format ? format.decimals : undefined;
      return decimals !== undefined
        ? `${pct.toFixed(decimals)}%`
        : `${Number(pct.toFixed(1))}%`;
    }

    case 'currency': {
      const code = (format && 'code' in format && format.code) || 'USD';
      const body = format && 'compact' in format && format.compact === false ? exact(value) : compact(value);
      return `${currencySymbol(code)}${body}`;
    }

    case 'rating': {
      const max = (format && 'max' in format && format.max) || 5;
      // One decimal is how a store shows a rating; trailing .0 reads as noise.
      return `${Number(value.toFixed(1))} / ${max}`;
    }

    case 'rank':
      return `#${Math.round(value).toLocaleString('en-GB')}`;

    case 'duration':
      return formatDuration(value);

    case 'bytes':
      return formatBytes(value);

    case 'text':
      return String(value);

    case 'number':
    default: {
      const decimals = format && 'decimals' in format ? format.decimals : undefined;
      if (decimals !== undefined) return value.toFixed(decimals);
      // Compact by default: tiles and marks are space-constrained, and the
      // exact figure is always one hover or one table-view click away.
      return format && 'compact' in format && format.compact === false ? exact(value) : compact(value);
    }
  }
};

/** True when this format wants stars drawn instead of a figure. */
export const isStarFormat = (format?: ValueFormat): format is { kind: 'rating'; max?: number } =>
  format?.kind === 'rating';

const SYMBOLS: Record<string, string> = { USD: '$', GBP: '£', EUR: '€', JPY: '¥' };

const currencySymbol = (code: string): string => SYMBOLS[code.toUpperCase()] ?? `${code.toUpperCase()} `;

/** Seconds → the largest unit that still says something: 90 → "1m 30s". */
const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${m}m ${rest}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes)) return '—';
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const sign = bytes < 0 ? '−' : '';
  return `${sign}${unit === 0 ? value : Number(value.toFixed(1))}${BYTE_UNITS[unit]}`;
};

const FORMAT_RULES: Array<[RegExp, ValueFormat]> = [
  // Counts first — see the note above.
  [/\bcount\b|_count|reviews?\b|installs?\b|downloads?\b/, { kind: 'number', compact: true }],
  [/rating|stars?\b|score\b/, { kind: 'rating', max: 5 }],
  [/\brank\b|_rank|position\b/, { kind: 'rank' }],
  [/revenue|price|earnings|spend|\bcost\b|usd\b/, { kind: 'currency', code: 'USD', compact: true }],
  [/percent|\bpct\b|_pct|\brate\b|share\b/, { kind: 'percent' }],
  [/\bbytes\b|size_bytes/, { kind: 'bytes' }],
  [/duration|seconds\b|_secs?\b/, { kind: 'duration' }],
];

/**
 * A sensible format for a column nobody has configured.
 *
 * Rule ORDER is load-bearing, and the counting rules come first on purpose:
 * `rating_count` and `review_count` both contain a word that would otherwise
 * win, and rendering a count of 1,024 as "1024 / 5" with four filled stars is
 * worse than not inferring at all. Returns undefined when nothing matches, so
 * the caller falls back to plain `number` rather than a guess.
 */
export const inferFormat = (...text: Array<string | undefined>): ValueFormat | undefined => {
  const hay = text.filter(Boolean).join(' ').toLowerCase();
  if (!hay) return undefined;

  for (const [pattern, format] of FORMAT_RULES) {
    if (pattern.test(hay)) return format;
  }
  return undefined;
};
